import { formatDateToMMDDYYYY } from './dateUtils';

/**
 * Whether a task's dates still fall inside its episode, and how to say so.
 *
 * Scheduling keeps a task's dates inside its episode window — the reschedule
 * endpoint rejects a target date outside it — but an episode-date edit can move
 * the window off a task that is already on the schedule, leaving it stranded
 * (the same records the episode edit impact dialog counts). Nothing recovers
 * automatically, so the task list and visit header flag it after the fact.
 */

/**
 * True when an ISO "YYYY-MM-DD" date falls outside the episode window. ISO date
 * strings compare correctly lexicographically, so no parsing is needed. An
 * unknown date or window is never "outside" — there is nothing to warn about.
 */
export const isDateOutsideEpisodeWindow = (
  date: string | null | undefined,
  episodeStartDate: string | null | undefined,
  episodeEndDate: string | null | undefined
): boolean => {
  if (!date || !episodeStartDate || !episodeEndDate) return false;
  return date < episodeStartDate || date > episodeEndDate;
};

export type TaskEpisodeWindowDates = {
  episodeStartDate: string | null | undefined;
  episodeEndDate: string | null | undefined;
  /** Date the task is scheduled for — the "Target Date" column. */
  targetDate: string | null | undefined;
  /** Documented visit date — the "Visit Date" column; visits only. */
  visitDate?: string | null | undefined;
};

/**
 * Explain which of a task's dates fall outside its episode, or `null` when it
 * sits inside the window (or the window is unknown). Names the offending date
 * and the window so the reader knows which value to fix and what to fix it to.
 */
export function describeDatesOutsideEpisodeWindow(
  dates: TaskEpisodeWindowDates
): string | null {
  const { episodeStartDate, episodeEndDate, targetDate, visitDate } = dates;
  const targetOutside = isDateOutsideEpisodeWindow(
    targetDate,
    episodeStartDate,
    episodeEndDate
  );
  const visitDateOutside = isDateOutsideEpisodeWindow(
    visitDate,
    episodeStartDate,
    episodeEndDate
  );
  if (!targetOutside && !visitDateOutside) return null;

  const window = `${formatDateToMMDDYYYY(episodeStartDate)} – ${formatDateToMMDDYYYY(
    episodeEndDate
  )}`;
  // Both dates are named individually unless they are the same day, which is the
  // common case for a visit that has not been rescheduled away from its target.
  if (targetOutside && visitDateOutside && targetDate !== visitDate) {
    return (
      `Target date ${formatDateToMMDDYYYY(targetDate)} and visit date ` +
      `${formatDateToMMDDYYYY(visitDate)} fall outside the episode dates (${window}).`
    );
  }
  const [label, date] = targetOutside
    ? ['Target date', targetDate]
    : ['Visit date', visitDate];
  return `${label} ${formatDateToMMDDYYYY(date)} falls outside the episode dates (${window}).`;
}
